import { css } from 'aphrodite';
import * as React from 'react';
import { Control, FormState, useWatch } from 'react-hook-form';
import { FormFieldType } from '../../../../../extViewmodels';
import { useConditionalVisibility } from '../../../../containers/SurveyResponses/shared/hooks/useConditionalVisibility';
import { IDonationSurvey, IPublicFileAttachment } from '../../../../models/Api';
import { IFormValues } from '../utils';
import { CustomFormFieldRenderer } from './CustomFormFieldRenderer';
import { styleSheet } from './styles';

type IProps = {
	survey: IDonationSurvey;
	control: Control<IFormValues>;
	formState: FormState<IFormValues>;
	setValue: (name: keyof IFormValues, value: string | number | boolean) => void;
	token: string;
};

export const CustomForm = ({ survey, control, formState, setValue, token }: IProps) => {
	// Watch customProperties so conditional-visibility evaluation re-runs when answers change.
	const watchedCustomProperties = useWatch({ control, name: 'customProperties' });
	const visibleFieldIds = useConditionalVisibility(survey.surveyForm, watchedCustomProperties ?? {});

	// Set of fieldIds represented in surveyForm.surveyParts. Used to distinguish
	// "field is governed by surveyForm and currently not visible" (hide + clear)
	// from "field is not represented in surveyForm at all" (show as default).
	// Without this, fields added to customForm.fields after surveyForm was last
	// saved — or surveys saved before surveyForm was wired up — would silently
	// disappear from the donor view.
	const surveyPartFieldIds = React.useMemo(() => {
		if (!survey.surveyForm?.surveyParts?.length) return undefined;
		return new Set(
			survey.surveyForm.surveyParts
				.map(part => (part as { fieldId?: string }).fieldId)
				.filter((id): id is string => !!id)
		);
	}, [survey.surveyForm]);

	// Clear values of fields that became hidden so submitted responses don't include stale answers.
	const prevVisibleRef = React.useRef<Set<string> | undefined>(undefined);
	React.useEffect(() => {
		const prev = prevVisibleRef.current;
		if (prev && visibleFieldIds) {
			for (const fieldId of prev) {
				if (!visibleFieldIds.has(fieldId)) {
					const field = survey.customForm?.fields?.find(f => f.id === fieldId);
					if (field) {
						const defaultValue: string | IPublicFileAttachment[] =
							field.fieldType === FormFieldType.FileUpload ? [] : '';
						// react-hook-form accepts dot-paths for nested keys, but the prop's setValue type
						// is narrowed to top-level keys; cast to satisfy the local signature.
						setValue(
							`customProperties.${fieldId}` as keyof IFormValues,
							defaultValue as unknown as string | number | boolean
						);
					}
				}
			}
		}
		// Only overwrite the ref when we have a defined set. If surveyForm transitions
		// defined → undefined → defined (e.g., it briefly becomes nullable), preserving
		// the last known set lets the next defined emission still detect newly-hidden
		// fields and clear them.
		if (visibleFieldIds) {
			prevVisibleRef.current = visibleFieldIds;
		}
	}, [visibleFieldIds, survey.customForm?.fields, setValue]);

	const fields = survey.customForm.fields?.filter(f => !f.isHidden) ?? [];
	const isFieldVisible = (fieldId: string) => {
		// No surveyForm at all → no filtering.
		if (!visibleFieldIds || !surveyPartFieldIds) return true;
		// Field isn't governed by surveyForm → default to visible.
		if (!surveyPartFieldIds.has(fieldId)) return true;
		return visibleFieldIds.has(fieldId);
	};

	return (
		<div>
			<div className={css(styleSheet.customFormSection)}>
				{fields
					.filter(field => isFieldVisible(field.id))
					.map(field => (
						<CustomFormFieldRenderer
							key={field.id}
							field={field}
							control={control}
							formState={formState}
							setValue={setValue}
							token={token}
						/>
					))}
			</div>
		</div>
	);
};
